import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface LossReason {
  id: string;
  account_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UseLossReasonsOptions {
  includeInactive?: boolean;
}

const getErrorMessage = (error: unknown) => (
  error instanceof Error ? error.message : 'Ocorreu um erro inesperado'
);

export function useLossReasons({ includeInactive = false }: UseLossReasonsOptions = {}) {
  const { user } = useAuth();
  const [lossReasons, setLossReasons] = useState<LossReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLossReasons = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      let query = supabase
        .from('account_loss_reasons')
        .select('*')
        .eq('account_id', user.account.id);

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error: fetchError } = await query.order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setLossReasons(data || []);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [includeInactive, user?.account?.id]);

  const createLossReason = async (name: string) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: createError } = await supabase
        .from('account_loss_reasons')
        .insert({
          account_id: user.account.id,
          name,
          is_active: true
        })
        .select()
        .single();

      if (createError) throw createError;

      setLossReasons(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      return data;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return null;
    }
  };

  const updateLossReason = async (id: string, updates: Partial<LossReason>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_loss_reasons')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchLossReasons();
      return true;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return false;
    }
  };

  const deleteLossReason = async (id: string) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_loss_reasons')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;
      setLossReasons(prev => includeInactive
        ? prev.map(reason => reason.id === id ? { ...reason, is_active: false } : reason)
        : prev.filter(reason => reason.id !== id)
      );
      return true;
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      return false;
    }
  };

  useEffect(() => {
    fetchLossReasons();
  }, [fetchLossReasons]);

  return {
    lossReasons,
    loading,
    error,
    fetchLossReasons,
    createLossReason,
    updateLossReason,
    deleteLossReason
  };
}
